/**
 * Nexus Revenue Page
 * Financial overview and reports
 */

import { useEffect, useState } from 'react'
import { nexusRevenueApi } from '@/services/nexusApi'

interface RevenuePeriod {
  period: string
  gross_amount: number
  cost_amount: number
  revenue_amount: number
  transaction_count: number
}

interface RevenueSummary {
  period_type: string
  start_date: string
  end_date: string
  periods: RevenuePeriod[]
  totals: {
    gross_amount: number
    cost_amount: number
    revenue_amount: number
    transaction_count: number
  }
}

interface RevenueByAbility {
  ability_name: string
  gross_amount: number
  cost_amount: number
  revenue_amount: number
  transaction_count: number
  percentage: number
}

export default function NexusRevenue() {
  const [summary, setSummary] = useState<RevenueSummary | null>(null)
  const [byAbility, setByAbility] = useState<RevenueByAbility[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily')

  useEffect(() => {
    loadData()
  }, [period])

  const loadData = async () => {
    setLoading(true)
    try {
      const [summaryData, abilityData] = await Promise.all([
        nexusRevenueApi.getSummary(period),
        nexusRevenueApi.getByAbility(),
      ])
      setSummary(summaryData)
      setByAbility(abilityData.items || [])
    } catch (error) {
      console.error('Failed to load revenue data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading && !summary) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-700 rounded w-48" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-slate-800 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const totalRevenue = summary?.totals?.revenue_amount || byAbility.reduce((sum, a) => sum + a.revenue_amount, 0)
  const totalGross = summary?.totals?.gross_amount || byAbility.reduce((sum, a) => sum + a.gross_amount, 0)
  const totalCost = summary?.totals?.cost_amount || byAbility.reduce((sum, a) => sum + a.cost_amount, 0)
  const totalTransactions = summary?.totals?.transaction_count || byAbility.reduce((sum, a) => sum + a.transaction_count, 0)

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue</h1>
          <p className="text-gray-400 mt-1">
            Financial overview and analytics
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['daily', 'weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                period === p
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-sm text-gray-400">Total Gross</p>
          <p className="text-3xl font-bold text-white mt-1">
            KES {totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-2">{totalTransactions} transactions</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-sm text-gray-400">Total Cost</p>
          <p className="text-3xl font-bold text-red-400 mt-1">
            KES {totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-green-500/30">
          <p className="text-sm text-green-400">Net Revenue</p>
          <p className="text-3xl font-bold text-green-400 mt-1">
            KES {totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-sm text-gray-400">Margin</p>
          <p className="text-3xl font-bold text-white mt-1">
            {totalGross > 0 ? ((totalRevenue / totalGross) * 100).toFixed(1) : 0}%
          </p>
        </div>
      </div>

      {/* Revenue by Ability */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Revenue by Ability</h2>
        </div>
        <table className="w-full">
          <thead className="bg-slate-900">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Ability
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Transactions
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Gross
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Cost
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Margin
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {byAbility.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                  No revenue data yet
                </td>
              </tr>
            ) : (
              byAbility.map((item) => (
                <tr key={item.ability_name} className="hover:bg-slate-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-white font-medium">{item.ability_name}</span>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-300">
                    {item.transaction_count}
                  </td>
                  <td className="px-6 py-4 text-right text-white">
                    KES {item.gross_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-right text-red-400">
                    KES {item.cost_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-right text-green-400 font-medium">
                    KES {item.revenue_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-300">
                    {item.gross_amount > 0
                      ? ((item.revenue_amount / item.gross_amount) * 100).toFixed(1)
                      : 0}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {byAbility.length > 0 && (
            <tfoot className="bg-slate-900">
              <tr>
                <td className="px-6 py-4 font-bold text-white">Total</td>
                <td className="px-6 py-4 text-right font-bold text-white">
                  {byAbility.reduce((sum, a) => sum + a.transaction_count, 0)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-white">
                  KES {totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-6 py-4 text-right font-bold text-red-400">
                  KES {totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-400">
                  KES {totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-6 py-4 text-right font-bold text-white">
                  {totalGross > 0 ? ((totalRevenue / totalGross) * 100).toFixed(1) : 0}%
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Revenue Trend */}
      {summary?.periods && summary.periods.length > 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Revenue Trend ({period})</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {summary.periods.slice(-10).map((p) => (
                <div key={p.period} className="flex items-center gap-4">
                  <span className="text-gray-400 w-24 text-sm">{p.period}</span>
                  <div className="flex-1 bg-slate-700 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-green-500 h-full rounded-full"
                      style={{
                        width: `${Math.min(100, (p.revenue_amount / (summary.totals.revenue_amount || 1)) * 100 * 3)}%`,
                      }}
                    />
                  </div>
                  <span className="text-green-400 font-medium w-32 text-right">
                    KES {p.revenue_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
